

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ── Save/Get token from localStorage ────────────────────────
export const saveToken   = (token) => localStorage.setItem('aamira_token', token);
export const getToken    = ()      => localStorage.getItem('aamira_token');
export const removeToken = ()      => localStorage.removeItem('aamira_token');
export const saveUser    = (user)  => localStorage.setItem('aamira_user', JSON.stringify(user));
export const getUser     = ()      => JSON.parse(localStorage.getItem('aamira_user') || 'null');
export const removeUser  = ()      => localStorage.removeItem('aamira_user');
export const getAdminToken = () => localStorage.getItem('aamira_admin_token');

const saveRefreshToken   = (token) => localStorage.setItem('aamira_refresh', token);
const getRefreshToken    = ()      => localStorage.getItem('aamira_refresh');
const removeRefreshToken = ()      => localStorage.removeItem('aamira_refresh');

// ── Register ─────────────────────────────────────────────────
export const register = async ({ name, email, password }) => {
  const res = await fetch(`${API}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, email, password }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data;
};

// ── Admin Login ──────────────────────────────────────────────
// Calls POST /api/admin/login
// Saves admin session separately from regular user session
export const adminLogin = async ({ email, password }) => {
  const res = await fetch(`${API}/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  localStorage.setItem('aamira_admin', JSON.stringify(data.user));
  localStorage.setItem('aamira_admin_token', data.token); 
  return data.user;
};

// ── Admin Logout ─────────────────────────────────────────────
export const adminLogout = async () => {
  await fetch(`${API}/admin/logout`, { method: 'POST' });
  localStorage.removeItem('aamira_admin');
  localStorage.removeItem('aamira_admin_token'); 
};

// ── isAdminAuthenticated (used by AdminRoute guard in App.jsx) ─
export const isAdminAuthenticated = () => {
  return localStorage.getItem('aamira_admin') !== null;
};

// ── Login ────────────────────────────────────────────────────
export const login = async ({ email, password }) => {
  const res = await fetch(`${API}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  
  saveToken(data.token);
  saveUser(data.user);
  if (data.refresh_token) saveRefreshToken(data.refresh_token);
  return data;
};




// ── Logout ───────────────────────────────────────────────────
export const logout = async () => {
  await fetch(`${API}/auth/logout`, { method: 'POST' });
  removeToken();
  removeUser();
  removeRefreshToken();
};


// ── Auto refresh session ─────────────────────────────────────
const refreshSession = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  try {
    const res = await fetch(`${API}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    const data = await res.json();
    if (!data.success) return null;
    saveToken(data.access_token);
    saveRefreshToken(data.refresh_token);
    return data.access_token;
  } catch {
    return null;
  }
};

// ── Forgot password — send OTP ───────────────────────────────
export const forgotPassword = async (email) => {
  const res = await fetch(`${API}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data;
};

// ── Reset password — verify OTP + set new password ───────────
export const resetPassword = async ({ email, otp, new_password }) => {
  const res = await fetch(`${API}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, otp, new_password }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data;
};

export const authFetch = async (url, options = {}) => {
  const makeReq = (token) => fetch(url, {
    ...options,
    headers: { ...options.headers, Authorization: `Bearer ${token}` },
  });

  let res = await makeReq(getToken());

  if (res.status === 401) {
    const newToken = await refreshSession();
    if (newToken) {
      res = await makeReq(newToken);
    } else {
      removeToken();
      removeUser();
      removeRefreshToken();
      window.location.href = '/account';
      return null;
    }
  }
  return res;
};

// ── Get profile ──────────────────────────────────────────────
export const getProfile = async () => {
  const res = await fetch(`${API}/auth/profile`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data.user;
};

// ── Update profile ───────────────────────────────────────────
export const updateProfile = async ({ name, phone }) => {
  const res = await fetch(`${API}/auth/profile`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify({ name, phone }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  // Backend returns full profile row — merge with existing saved user
  const existing = getUser() || {};
  const updated = { ...existing, ...data.user };
  saveUser(updated);
  return updated;
};


export const createRazorpayOrder = async ({ items, address_id, gift_wrap = false, coupon = '' }) => {
  const res = await fetch(`${API}/orders/create-order`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify({ items, address_id, gift_wrap, coupon }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data;
};




export const confirmOrder = async ({ razorpay_order_id, razorpay_payment_id, razorpay_signature, pendingOrder }) => {
  const res = await fetch(`${API}/orders/confirm`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify({ razorpay_order_id, razorpay_payment_id, razorpay_signature, pendingOrder }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data.order;
};

// ── Get Single Order ─────────────────────────────────────────
export const getOrder = async (id) => {
  const res = await fetch(`${API}/orders/${id}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data.order;
};

// ── Change password ──────────────────────────────────────────
export const changePassword = async (new_password) => {
  const res = await fetch(`${API}/auth/change-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify({ new_password }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data;
};

// ── Get orders ───────────────────────────────────────────────
// Backend returns raw Supabase rows from orders table
export const getOrders = async () => {
  const res = await authFetch(`${API}/user/orders`);
  if (!res) return [];
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data.orders;
};


export const getWishlist = async () => {
  const res = await authFetch(`${API}/user/wishlist`);
  if (!res) return [];
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data.wishlist.map(item => ({
  id:    item.product_id,
  name:  item.products?.name,
  price: item.products?.sale_price || item.products?.original_price,
  image: item.products?.images?.[0] || item.products?.image_1 || '',
}));
};

// ── Add to wishlist ──────────────────────────────────────────
export const addToWishlist = async (product_id) => {
  const res = await fetch(`${API}/user/wishlist`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify({ product_id }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data;
};

// ── Remove from wishlist ─────────────────────────────────────
export const removeFromWishlist = async (product_id) => {
  const res = await fetch(`${API}/user/wishlist/${product_id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data;
};

// ── Get addresses ────────────────────────────────────────────
// Backend returns rows: { id, label, full_name, phone, line_1, line_2, city, state, pincode, is_default }
export const getAddresses = async () => {
  const res = await authFetch(`${API}/user/addresses`);
  if (!res) return [];
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data.addresses;
};

// ── Add address ──────────────────────────────────────────────
// Backend expects: { label, full_name, phone, line_1, line_2, city, state, pincode, is_default }
export const addAddress = async (address) => {
  const res = await fetch(`${API}/user/addresses`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(address),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data.address;
};

// ── Delete address ───────────────────────────────────────────
export const deleteAddress = async (id) => {
  const res = await fetch(`${API}/user/addresses/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data;
};


// GET cart
export const getCart = async () => {
  const res = await fetch(`${API}/user/cart`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data.cart.map(item => ({
    ...item.products,
    cartItemId: item.id,
    _originalQty: item.qty,
    qty: Math.min(item.qty, item.products?.stock_qty ?? item.qty),
    color: item.color || null,   // ← add
    size:  item.size  || null,   // ← add
    image: item.products?.images?.[0] || item.products?.image_1 || null,
    price: item.products?.sale_price || item.products?.original_price,
    mrp:   item.products?.sale_price ? item.products?.original_price : null,
    maxQty: item.products?.stock_qty ?? null, 
  }));
};

// Add to cart
export const addToCart = async (product_id, qty = 1, color = null, size = null) => {
  const res = await fetch(`${API}/user/cart`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify({ product_id, qty, color, size }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data;
};

// ── Verify OTP ───────────────────────────────────────────────
export const verifyOTP = async ({ email, otp }) => {
  const res = await fetch(`${API}/auth/verify-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, otp }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data;
};

// Update qty
export const updateCartQty = async (cartItemId, qty) => {
  const res = await fetch(`${API}/user/cart/${cartItemId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify({ qty }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data;
};

// Remove item
export const removeFromCart = async (cartItemId) => {
  const res = await fetch(`${API}/user/cart/${cartItemId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data;
};

// Clear cart
export const clearCart = async () => {
  const res = await fetch(`${API}/user/cart`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data;
};